export const AT45DB_FLASH_BYTES = 540_672;
export const DATAFLASH_PAGE_BYTES = 264;
export const PAGE_HEADER_BYTES = 10;
export const BLOCK_HEADER_BYTES = 4;
export const BLOCK_PAYLOAD_BYTES = 248;
export const PAGE_FOOTER_BYTES = 2;
export const FILE_DESCRIPTOR_BYTES = 72;
export const FILENAME_FIELD_OFFSET = 1;
export const FILENAME_FIELD_BYTES = 18;

export interface FlashSlotSummary {
  readonly fileId: number;
  readonly filename: string;
  readonly blockCount: number;
  readonly capacityBytes: number;
}

export interface FlashInstallResult {
  readonly flash: Uint8Array;
  readonly filename: string;
  readonly replacedFilename: string;
  readonly fileId: number;
  readonly blockCount: number;
  readonly capacityBytes: number;
  readonly appBytes: number;
}

interface PageBlock {
  readonly page: number;
  readonly fileId: number;
  readonly index: number;
  readonly flags: number;
  readonly payload: Uint8Array;
}

interface FileSlot {
  readonly fileId: number;
  readonly filename: string;
  readonly blocks: PageBlock[];
}

const CRC32_TABLE = makeCrc32Table();

export function isAuthenticFlashImage(bytes: Uint8Array): boolean {
  return bytes.byteLength === AT45DB_FLASH_BYTES;
}

export function isCybikoAppArchive(bytes: Uint8Array): boolean {
  return bytes.byteLength >= 4 && bytes[0] === 0x43 && bytes[1] === 0x79;
}

export function normalizeByorAppFilename(name: string): string | null {
  const dot = name.lastIndexOf('.');
  const rawBase = dot >= 0 ? name.slice(0, dot) : name;
  const rawExt = dot >= 0 ? name.slice(dot + 1) : 'app';
  const base = rawBase
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '')
    .slice(0, 8);
  const ext = rawExt
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 3) || 'app';
  if (!base) return null;
  return `${base}.${ext}`;
}

export function normalizeFlashFilename(name: string): string | null {
  const lowered = name.trim().toLowerCase();
  if (!/^[a-z0-9_-]{1,16}\.[a-z0-9_]{1,4}$/.test(lowered)) return null;
  if (asciiBytes(lowered).byteLength > FILENAME_FIELD_BYTES) return null;
  return lowered;
}

export function listFlashSlots(flashInput: Uint8Array): FlashSlotSummary[] {
  const flash = copyBytes(flashInput);
  assertAuthenticFlash(flash);
  const slots: FlashSlotSummary[] = [];
  const seen = new Set<number>();

  for (let page = 0; page < pageCount(flash); page += 1) {
    const block = readPageBlock(flash, page);
    if (!block || !isLivePage(block) || block.index !== 0 || seen.has(block.fileId)) continue;
    const filename = readDescriptorFilename(block.payload);
    if (!filename) continue;
    const blocks = readBlocksForFile(flash, block.fileId);
    seen.add(block.fileId);
    slots.push({
      fileId: block.fileId,
      filename,
      blockCount: blocks.length,
      capacityBytes: blocks.length * BLOCK_PAYLOAD_BYTES - FILE_DESCRIPTOR_BYTES,
    });
  }

  return slots.sort((a, b) => a.filename.localeCompare(b.filename));
}

export function installAppIntoFlash(
  flashInput: Uint8Array,
  appInput: Uint8Array,
  options: { readonly filename: string; readonly replace: string },
): FlashInstallResult {
  const flash = copyBytes(flashInput);
  const app = copyBytes(appInput);
  assertAuthenticFlash(flash);
  if (!isCybikoAppArchive(app)) {
    throw new Error('App archive does not start with the Cybiko filer magic "Cy"');
  }

  const filename = normalizeFlashFilename(options.filename);
  const replace = normalizeFlashFilename(options.replace);
  if (!filename) throw new Error(`Invalid CyOS app filename: ${options.filename}`);
  if (!replace) throw new Error(`Invalid replacement slot filename: ${options.replace}`);

  const slot = findFileSlot(flash, replace);
  if (!slot) throw new Error(`Could not find replacement slot: ${replace}`);

  const requiredBlocks = Math.ceil((FILE_DESCRIPTOR_BYTES + app.byteLength) / BLOCK_PAYLOAD_BYTES);
  const capacityBytes = slot.blocks.length * BLOCK_PAYLOAD_BYTES - FILE_DESCRIPTOR_BYTES;
  if (app.byteLength > capacityBytes) {
    throw new Error(`${filename} is ${app.byteLength} bytes but ${replace} has only ${capacityBytes} bytes`);
  }

  const fileBytes = readSlotBytes(slot.blocks);
  const descriptor = copyBytes(fileBytes.subarray(0, FILE_DESCRIPTOR_BYTES));
  writeFilenameField(descriptor, filename);

  const replacement = new Uint8Array(requiredBlocks * BLOCK_PAYLOAD_BYTES);
  replacement.fill(0xff);
  replacement.set(descriptor, 0);
  replacement.set(app, FILE_DESCRIPTOR_BYTES);

  const allocatedPages = slot.blocks.map((block) => block.page).slice(0, requiredBlocks);
  const allocatedSet = new Set(allocatedPages);
  const releasedPages = slot.blocks
    .map((block) => block.page)
    .filter((page) => !allocatedSet.has(page));

  for (let index = 0; index < allocatedPages.length; index += 1) {
    const page = allocatedPages[index]!;
    const srcOffset = index * BLOCK_PAYLOAD_BYTES;
    const dstOffset = page * DATAFLASH_PAGE_BYTES + PAGE_HEADER_BYTES + BLOCK_HEADER_BYTES;
    writeAllocatedPageHeader(flash, page, slot.fileId, index);
    flash.set(replacement.subarray(srcOffset, srcOffset + BLOCK_PAYLOAD_BYTES), dstOffset);
    writePageChecksum(flash, page);
  }

  for (const page of releasedPages) {
    writeReleasedPage(flash, page);
  }

  return {
    flash,
    filename,
    replacedFilename: replace,
    fileId: slot.fileId,
    blockCount: allocatedPages.length,
    capacityBytes,
    appBytes: app.byteLength,
  };
}

function findFileSlot(flash: Uint8Array, filename: string): FileSlot | null {
  const normalized = normalizeFlashFilename(filename);
  if (!normalized) return null;

  for (let page = 0; page < pageCount(flash); page += 1) {
    const block = readPageBlock(flash, page);
    if (!block || !isLivePage(block) || block.index !== 0) continue;
    if (readDescriptorFilename(block.payload) !== normalized) continue;
    return {
      fileId: block.fileId,
      filename: normalized,
      blocks: readBlocksForFile(flash, block.fileId),
    };
  }

  return null;
}

function readBlocksForFile(flash: Uint8Array, fileId: number): PageBlock[] {
  const blocks: PageBlock[] = [];
  for (let page = 0; page < pageCount(flash); page += 1) {
    const block = readPageBlock(flash, page);
    if (!block || !isLivePage(block) || block.fileId !== fileId) continue;
    blocks.push(block);
  }
  return blocks.sort((a, b) => a.index - b.index);
}

function readSlotBytes(blocks: readonly PageBlock[]): Uint8Array {
  const out = new Uint8Array(blocks.length * BLOCK_PAYLOAD_BYTES);
  for (let index = 0; index < blocks.length; index += 1) {
    out.set(blocks[index]!.payload, index * BLOCK_PAYLOAD_BYTES);
  }
  return out;
}

function readPageBlock(flash: Uint8Array, page: number): PageBlock | null {
  const offset = page * DATAFLASH_PAGE_BYTES;
  if (offset + DATAFLASH_PAGE_BYTES > flash.byteLength) return null;
  const footer = readUInt16BE(flash, offset + DATAFLASH_PAGE_BYTES - PAGE_FOOTER_BYTES);
  if (footer !== 0x4fbe) return null;
  const payloadStart = offset + PAGE_HEADER_BYTES + BLOCK_HEADER_BYTES;
  return {
    page,
    fileId: readUInt16BE(flash, offset + PAGE_HEADER_BYTES),
    index: readUInt16BE(flash, offset + PAGE_HEADER_BYTES + 2),
    flags: readUInt16BE(flash, offset + 8),
    payload: flash.subarray(payloadStart, payloadStart + BLOCK_PAYLOAD_BYTES),
  };
}

function readDescriptorFilename(payload: Uint8Array): string | null {
  if (payload[0] !== 0x00) return null;
  const field = payload.subarray(FILENAME_FIELD_OFFSET, FILENAME_FIELD_OFFSET + FILENAME_FIELD_BYTES);
  const nul = field.indexOf(0x00);
  const raw = field.subarray(0, nul >= 0 ? nul : field.byteLength);
  return normalizeFlashFilename(String.fromCharCode(...raw));
}

function writeFilenameField(descriptor: Uint8Array, filename: string): void {
  const nameBytes = asciiBytes(filename);
  descriptor.fill(0x00, FILENAME_FIELD_OFFSET, FILENAME_FIELD_OFFSET + FILENAME_FIELD_BYTES);
  descriptor.set(nameBytes, FILENAME_FIELD_OFFSET);
}

function writeAllocatedPageHeader(flash: Uint8Array, page: number, fileId: number, index: number): void {
  const offset = page * DATAFLASH_PAGE_BYTES;
  writeUInt16BE(flash, offset + 8, index === 0 ? 0x80b0 : 0x80f8);
  writeUInt16BE(flash, offset + PAGE_HEADER_BYTES, fileId);
  writeUInt16BE(flash, offset + PAGE_HEADER_BYTES + 2, index);
}

function writeReleasedPage(flash: Uint8Array, page: number): void {
  const offset = page * DATAFLASH_PAGE_BYTES;
  writeUInt16BE(flash, offset + 8, 0);
  writeUInt16BE(flash, offset + PAGE_HEADER_BYTES, 0);
  writeUInt16BE(flash, offset + PAGE_HEADER_BYTES + 2, 0);
  flash.fill(0xff, offset + PAGE_HEADER_BYTES + BLOCK_HEADER_BYTES, offset + DATAFLASH_PAGE_BYTES - PAGE_FOOTER_BYTES);
  writePageChecksum(flash, page);
}

function writePageChecksum(flash: Uint8Array, page: number): void {
  const offset = page * DATAFLASH_PAGE_BYTES;
  const checksum = crc32(flash.subarray(offset + 8, offset + DATAFLASH_PAGE_BYTES - PAGE_FOOTER_BYTES));
  writeUInt32BE(flash, offset, checksum);
}

function isLivePage(block: PageBlock): boolean {
  return (block.flags & 0x8000) !== 0;
}

function pageCount(flash: Uint8Array): number {
  return Math.floor(flash.byteLength / DATAFLASH_PAGE_BYTES);
}

function assertAuthenticFlash(bytes: Uint8Array): void {
  if (bytes.byteLength !== AT45DB_FLASH_BYTES) {
    throw new Error(`Expected ${AT45DB_FLASH_BYTES} bytes of AT45DB041 flash, got ${bytes.byteLength}`);
  }
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = CRC32_TABLE[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function makeCrc32Table(): Uint32Array {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[n] = c >>> 0;
  }
  return table;
}

function readUInt16BE(bytes: Uint8Array, offset: number): number {
  return (bytes[offset]! << 8) | bytes[offset + 1]!;
}

function writeUInt16BE(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = (value >>> 8) & 0xff;
  bytes[offset + 1] = value & 0xff;
}

function writeUInt32BE(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = (value >>> 24) & 0xff;
  bytes[offset + 1] = (value >>> 16) & 0xff;
  bytes[offset + 2] = (value >>> 8) & 0xff;
  bytes[offset + 3] = value & 0xff;
}

function asciiBytes(value: string): Uint8Array {
  const bytes = new Uint8Array(value.length);
  for (let index = 0; index < value.length; index += 1) {
    bytes[index] = value.charCodeAt(index) & 0x7f;
  }
  return bytes;
}

function copyBytes(bytes: Uint8Array): Uint8Array {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
}
